import { Injectable } from '@angular/core';

import { UserRole } from '@shared/enums/user-role.enum';
import { AuthService } from '@core/services/auth.service';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { ShipmentsListType } from '@shared/enums/shipments-list-type.enum';
import { RolesConstants } from '@shared/model/roles-constants';
import { Observable, of } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  constructor(private authService: AuthService) {}

    /**
   * Reactive variant of {@link isUserHasRoles}. When a session exists (valid OR
   * expired token) but `currentUser$` hasn't been hydrated yet — e.g. on a fresh
   * tab while the token is being restored/refreshed — it waits for the user to be
   * populated before evaluating, so callers (route guards) don't wrongly deny an
   * already-authenticated user. Falls back to a synchronous decision when the
   * user is already available or there is no session at all.
   */
  isUserHasRoles$(roles: UserRole[]): Observable<boolean> {
    if (this.authService.currentUser$.value) {
      return of(this.isUserHasRoles(roles));
    }
    return this.authService.currentUser$.pipe(
      filter((user) => !!user),
      take(1),
      map(() => this.isUserHasRoles(roles))
    );
  }

  isUserHasRoles(roles: UserRole[]): boolean {
    if (!roles || !roles?.length) return false;
    for (const role of roles) {
      const userRole = (this.authService.currentUser$.value?.userRoles || []).find((item) => item === role);

      if (userRole) {
        return true;
      }
    }
    return false;
  }

  /**
   * The first route the current user's role is actually allowed to land on.
   *
   * This is the single source of truth for "where is home for this user". It is
   * reused wherever the app picks a destination on the user's behalf rather than
   * at their explicit request — the login landing branch, and the role-aware
   * home redirect — so a role that cannot see the dashboard (e.g. FLEET) is never
   * dropped on /access-denied by the app's own default routing.
   */
  landingRoute(): string {
    return this.isUserHasRoles(RolesConstants.DASHBOARD_VIEW)
      ? RoutesUtil.Dashboards.url()
      : RoutesUtil.B2bShipmentsList.url({ type: ShipmentsListType.All});
  }

}
